import { ButtonColor } from '../components/Button/Button.constants';
import { ButtonLayoutAlignment } from '../components/ButtonLayout/ButtonLayout.constants';
import { CurrencyFieldVariant } from '../components/CurrencyField/CurrencyField.constants';
import { CalendarTriggerPosition } from '../components/DatePicker/CalendarTrigger/CalendarTrigger.constants';
import { DatePickerVariant } from '../components/DatePicker/DatePicker.constants';
import { IconButtonBackgroundOpacity, IconButtonColor, IconButtonIconSize } from '../components/IconButton/IconButton.constants';
import { MenuColor } from '../components/Menu/Menu.constants';
import { RadioLabelSize } from '../components/Radio/Radio.constants';
import { SelectMenuItemVariant } from '../components/Select/SelectMenu/SelectMenu.constants';
import { TextTransform, TypographyType, TypographyTypeOrTag } from '../components/Text/Text.constants';
import { TextFieldBorderColor } from '../components/TextField/TextField.constants';
import { TileBackgroundColor } from '../components/Tile/Tile.constants';
import { TooltipVariant } from '../components/Tooltip/Tooltip.constants';
import { AccordionColor, BoxBackgroundColor, ButtonSize, ButtonType, CheckboxLabelSize, FontColor, FontSize, FontWeight, GlobalNotificationType, IconButtonType, IconColor, IconSize, LineHeight, LinkColor, PopoverColor, PopoverPosition, ProgressColor, SnackbarType, Spacing, StatusIndicatorColor, SwitchColor, SymbolColor, TableBackgroundColorVariant, TableSortDirection, TextFieldVariant, TooltipPlacement, UnorderedListLineHeight } from '../enums';
import { Calendar, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Cross, InfoRound, Lock, MagnifyingGlass, MinusRound, PlusRound, QuestionMarkRound, TickRound, WarningTriangle } from '../icons';
import { palette, reversedPalette } from '../palette';
import { gradients } from '../palette/gradients';
import { shadows } from '../palette/shadows';
import { hexToRgb, px, rem } from '../utils';
import { COMMON_THEME_COLORS, THEME_FONTS } from './themes.constants';
export function generateTheme(themeName, segmentName, colorMapping, segmentColors, segmentShadows) {
  return {
    name: themeName,
    segment: segmentName,
    ...THEME_FONTS,
    colorMapping: colorMapping,
    Main: {
      ...segmentColors,
      ...COMMON_THEME_COLORS,
      primaryBackground: segmentColors.mono100,
      primaryBackgroundMobile: segmentColors.mono100,
      secondaryBackground: segmentColors.mono200,
      secondaryBackgroundMobile: segmentColors.mono200,
      tertiaryBackground: segmentColors.mono100,
      tertiaryBackgroundMobile: segmentColors.mono100,
      color: segmentColors.mono900,
      fontWeight: FontWeight.WEIGHT_500,
      outline: {
        color: segmentColors.positiveContrast
      }
    },
    Box: {
      backgrounds: {
        [BoxBackgroundColor.MONO_100]: segmentColors.mono100,
        [BoxBackgroundColor.MONO_200]: segmentColors.mono200,
        [BoxBackgroundColor.MONO_900]: segmentColors.mono900,
        [BoxBackgroundColor.NEGATIVE]: segmentColors.negative,
        [BoxBackgroundColor.POSITIVE]: segmentColors.positive,
        [BoxBackgroundColor.PRIMARY]: segmentColors.primary,
        [BoxBackgroundColor.SECONDARY]: segmentColors.secondary,
        [BoxBackgroundColor.ATTENTION]: segmentColors.attention
      }
    },
    Icon: {
      iconColorToPaletteColor: {
        [IconColor.PRIMARY_CONTRAST]: segmentColors.primaryContrast,
        [IconColor.SECONDARY_CONTRAST]: segmentColors.secondaryContrast,
        [IconColor.POSITIVE_CONTRAST]: segmentColors.positiveContrast,
        [IconColor.NEGATIVE_CONTRAST]: segmentColors.negativeContrast,
        [IconColor.ATTENTION_CONTRAST]: segmentColors.attentionContrast,
        [IconColor.LIGHT]: COMMON_THEME_COLORS.light,
        [IconColor.DARK]: COMMON_THEME_COLORS.dark,
        [IconColor.MONO_100]: segmentColors.mono100,
        [IconColor.MONO_500]: segmentColors.mono500,
        [IconColor.MONO_600]: segmentColors.mono600,
        [IconColor.MONO_900]: segmentColors.mono900
      },
      defaultVariant: IconColor.MONO_600
    },
    Accordion: {
      COLOR_TO_BACKGROUND_COLOR: {
        [AccordionColor.MONO_200]: segmentColors.mono200,
        [AccordionColor.MONO_100]: segmentColors.mono100
      },
      Item: {
        backgroundColor: segmentColors.mono200,
        border: {
          color: segmentColors.mono500
        },
        shadow: segmentColors.mono900
      }
    },
    Dropzone: {
      borderColor: segmentColors.mono500
    },
    Chip: {
      borderWidth: 1.5,
      textColor: segmentColors.secondaryContrast,
      backgroundColor: palette.transparent,
      borderColor: segmentColors.mono500,
      iconColor: IconColor.SECONDARY_CONTRAST,
      fontFamily: THEME_FONTS.fontFamilySecondary,
      fontWeight: FontWeight.WEIGHT_600,
      isFontFixApplied: false,
      normal: {
        fontSize: FontSize.SIZE_16,
        lineHeight: LineHeight.HEIGHT_24,
        padding: `${px(Spacing.SPACE_4)} ${px(Spacing.SPACE_16)}`,
        height: 32
      },
      dense: {
        fontSize: FontSize.SIZE_12,
        lineHeight: LineHeight.HEIGHT_16,
        padding: `${px(Spacing.SPACE_4)} ${px(Spacing.SPACE_8)}`,
        height: 24
      },
      hover: {
        textColor: segmentColors.secondaryContrastDarken,
        backgroundColor: segmentColors.mono500
      },
      selected: {
        textColor: palette.solidWhite,
        backgroundColor: segmentColors.secondary,
        iconColor: IconColor.LIGHT,
        hover: {
          textColor: palette.solidWhite,
          backgroundColor: segmentColors.secondaryDarken,
          borderColor: segmentColors.secondaryDarken
        },
        disabled: {
          textColor: segmentColors.mono100,
          backgroundColor: segmentColors.mono500,
          borderColor: segmentColors.mono500
        }
      },
      disabled: {
        textColor: segmentColors.mono500,
        backgroundColor: palette.transparent,
        iconColor: IconColor.MONO_500,
        borderColor: segmentColors.mono500
      }
    },
    CookieWidget: {
      backgroundColor: segmentColors.mono100
    },
    Carousel: {
      Navigation: {
        iconColor: {
          enabled: IconColor.SECONDARY_CONTRAST
        },
        buttonType: IconButtonType.TRANSPARENT
      },
      Pagination: {
        Bullet: {
          backgroundColor: `rgba(${hexToRgb(segmentColors.secondaryContrast)}, 0.3)`,
          active: {
            backgroundColor: `rgba(${hexToRgb(segmentColors.secondaryContrast)}, 1)`
          }
        }
      }
    },
    Tag: {
      backgroundColor: segmentColors.secondary,
      TAG_COLOR_TO_ICON_COLOR: {
        solidWhite: IconColor.LIGHT,
        zeusBlackUI: IconColor.DARK
      },
      fontWeight: FontWeight.WEIGHT_600,
      borderColor: segmentColors.mono500,
      color: segmentColors.mono600
    },
    DatePicker: {
      MobileDatePicker: {
        border: 'none',
        nativeIconInvertValue: 0
      },
      DesktopDatePicker: {
        input: {
          padding: `${px(Spacing.SPACE_8)} ${px(Spacing.SPACE_8)} ${px(Spacing.SPACE_8)} ${px(Spacing.SPACE_16)}`
        },
        navigationNext: {
          iconComponent: ChevronRight,
          iconColor: IconColor.SECONDARY_CONTRAST
        },
        navigationPrevious: {
          iconComponent: ChevronLeft,
          iconColor: IconColor.SECONDARY_CONTRAST
        },
        openMenu: {
          iconComponent: ChevronDown,
          iconColor: IconColor.SECONDARY_CONTRAST
        },
        closeMenu: {
          iconComponent: ChevronUp,
          iconColor: IconColor.SECONDARY_CONTRAST
        }
      },
      CalendarTrigger: {
        iconComponent: Calendar,
        iconColor: IconColor.SECONDARY_CONTRAST,
        placement: CalendarTriggerPosition.INSIDE,
        VARIANT_TO_MARGIN: {
          [DatePickerVariant.DENSE]: {
            marginRight: 0,
            marginLeft: 0
          },
          [DatePickerVariant.NORMAL]: {
            marginRight: 0,
            marginLeft: 0
          }
        }
      },
      CalendarDay: {
        background: palette.transparent,
        color: segmentColors.mono900,
        hover: {
          color: segmentColors.mono900,
          background: palette.transparent,
          border: `solid 1.5px ${segmentColors.mono500}`
        },
        selected: {
          background: segmentColors.secondary,
          color: palette.solidWhite,
          hover: {
            color: palette.solidWhite,
            background: segmentColors.secondaryDarken
          }
        },
        disabled: {
          background: palette.transparent,
          color: segmentColors.mono500
        },
        highlighted: {
          background: palette.transparent,
          color: segmentColors.secondaryContrast,
          hover: {
            color: segmentColors.secondaryContrastDarken
          }
        }
      },
      DayPicker: {
        boxShadow: segmentShadows.common,
        weekHeader: {
          fontFamily: THEME_FONTS.fontFamilyPrimary,
          fontWeight: FontWeight.WEIGHT_600
        }
      },
      CalendarMonth: {
        caption: {
          colorName: FontColor.MONO_900,
          fontFamily: THEME_FONTS.fontFamilyPrimary,
          fontSize: px(16),
          fontWeight: FontWeight.WEIGHT_600,
          textTransform: 'lowercase'
        },
        table: {
          fontFamily: THEME_FONTS.fontFamilyPrimary,
          fontWeight: FontWeight.WEIGHT_600,
          color: segmentColors.mono900
        }
      }
    },
    Button: {
      primary: {
        withIcon: null,
        minWidth: 120,
        fullWidth: 240,
        horizontalPadding: 16,
        borderRadius: 22,
        backgroundColor: segmentColors.primary,
        border: 'none',
        fontSize: FontSize.SIZE_16,
        fontWeight: FontWeight.WEIGHT_600,
        fontFamily: THEME_FONTS.fontFamilySecondary,
        textTransform: 'none',
        isRippleEffectEnabled: true,
        isPressingEffectEnabled: true,
        isFontFixApplied: false,
        hover: {
          backgroundColor: segmentColors.primaryDarken,
          BUTTON_COLOR_TO_COLOR: {
            [ButtonColor.DARK]: COMMON_THEME_COLORS.light,
            [ButtonColor.MONO_900]: palette.solidWhite,
            [ButtonColor.SECONDARY_CONTRAST]: palette.solidWhite,
            [ButtonColor.PRIMARY_CONTRAST]: palette.solidWhite,
            [ButtonColor.LIGHT]: COMMON_THEME_COLORS.light
          }
        },
        focus: {
          backgroundColor: segmentColors.primaryDarken,
          BUTTON_COLOR_TO_COLOR: {
            [ButtonColor.DARK]: COMMON_THEME_COLORS.light,
            [ButtonColor.MONO_900]: palette.solidWhite,
            [ButtonColor.SECONDARY_CONTRAST]: palette.solidWhite,
            [ButtonColor.PRIMARY_CONTRAST]: palette.solidWhite,
            [ButtonColor.LIGHT]: COMMON_THEME_COLORS.light
          }
        },
        disabled: {
          color: segmentColors.mono100,
          background: segmentColors.mono500,
          iconColor: IconColor.MONO_100
        },
        BUTTON_SIZE_TO_LINE_HEIGHT: {
          [ButtonSize.SMALL]: LineHeight.HEIGHT_24,
          [ButtonSize.NORMAL]: LineHeight.HEIGHT_24
        },
        BUTTON_COLOR_TO_COLOR: {
          [ButtonColor.DARK]: COMMON_THEME_COLORS.light,
          [ButtonColor.MONO_900]: palette.solidWhite,
          [ButtonColor.SECONDARY_CONTRAST]: palette.solidWhite,
          [ButtonColor.PRIMARY_CONTRAST]: palette.solidWhite,
          [ButtonColor.LIGHT]: COMMON_THEME_COLORS.light
        },
        BUTTON_COLOR_TO_ICON_COLOR: {
          [ButtonColor.DARK]: IconColor.LIGHT,
          [ButtonColor.MONO_900]: IconColor.LIGHT,
          [ButtonColor.SECONDARY_CONTRAST]: IconColor.LIGHT,
          [ButtonColor.PRIMARY_CONTRAST]: IconColor.LIGHT,
          [ButtonColor.LIGHT]: IconColor.LIGHT
        }
      },
      secondary: {
        withIcon: null,
        minWidth: 120,
        fullWidth: 240,
        horizontalPadding: 16,
        borderRadius: 22,
        backgroundColor: palette.transparent,
        border: `solid 1.5px ${segmentColors.mono500}`,
        fontSize: FontSize.SIZE_16,
        fontWeight: FontWeight.WEIGHT_600,
        fontFamily: THEME_FONTS.fontFamilySecondary,
        textTransform: 'none',
        isRippleEffectEnabled: true,
        isPressingEffectEnabled: true,
        isFontFixApplied: false,
        hover: {
          backgroundColor: segmentColors.mono500,
          BUTTON_COLOR_TO_COLOR: {
            [ButtonColor.DARK]: COMMON_THEME_COLORS.dark,
            [ButtonColor.MONO_900]: palette.zeusBlackUI,
            [ButtonColor.SECONDARY_CONTRAST]: segmentColors.secondaryContrastDarken,
            [ButtonColor.PRIMARY_CONTRAST]: segmentColors.primaryContrastDarken,
            [ButtonColor.LIGHT]: COMMON_THEME_COLORS.light
          }
        },
        focus: {
          backgroundColor: segmentColors.mono500,
          BUTTON_COLOR_TO_COLOR: {
            [ButtonColor.DARK]: COMMON_THEME_COLORS.dark,
            [ButtonColor.MONO_900]: palette.zeusBlackUI,
            [ButtonColor.SECONDARY_CONTRAST]: segmentColors.secondaryContrastDarken,
            [ButtonColor.PRIMARY_CONTRAST]: segmentColors.primaryContrastDarken,
            [ButtonColor.LIGHT]: palette.brightRedUI
          }
        },
        disabled: {
          color: segmentColors.mono500,
          background: palette.transparent,
          iconColor: IconColor.MONO_500
        },
        BUTTON_SIZE_TO_LINE_HEIGHT: {
          [ButtonSize.SMALL]: LineHeight.HEIGHT_24,
          [ButtonSize.NORMAL]: LineHeight.HEIGHT_24
        },
        BUTTON_COLOR_TO_COLOR: {
          [ButtonColor.DARK]: segmentColors.primaryContrast,
          [ButtonColor.MONO_900]: segmentColors.primaryContrast,
          [ButtonColor.SECONDARY_CONTRAST]: segmentColors.primaryContrast,
          [ButtonColor.PRIMARY_CONTRAST]: segmentColors.primaryContrast,
          [ButtonColor.LIGHT]: segmentColors.primaryContrast
        },
        BUTTON_COLOR_TO_ICON_COLOR: {
          [ButtonColor.DARK]: IconColor.PRIMARY_CONTRAST,
          [ButtonColor.MONO_900]: IconColor.PRIMARY_CONTRAST,
          [ButtonColor.SECONDARY_CONTRAST]: IconColor.PRIMARY_CONTRAST,
          [ButtonColor.PRIMARY_CONTRAST]: IconColor.PRIMARY_CONTRAST,
          [ButtonColor.LIGHT]: IconColor.PRIMARY_CONTRAST
        }
      },
      tertiary: {
        withIcon: null,
        minWidth: 'auto',
        fullWidth: 'auto',
        horizontalPadding: 0,
        borderRadius: 0,
        backgroundColor: 'transparent',
        border: 'none',
        fontSize: {
          [ButtonSize.NORMAL]: FontSize.SIZE_16,
          [ButtonSize.SMALL]: FontSize.SIZE_12
        },
        fontWeight: FontWeight.WEIGHT_600,
        fontFamily: THEME_FONTS.fontFamilySecondary,
        textTransform: 'none',
        isRippleEffectEnabled: false,
        isPressingEffectEnabled: false,
        isFontFixApplied: false,
        hover: {
          backgroundColor: 'transparent',
          BUTTON_COLOR_TO_COLOR: {
            [ButtonColor.DARK]: COMMON_THEME_COLORS.dark,
            [ButtonColor.SECONDARY_CONTRAST]: segmentColors.secondaryContrastDarken,
            [ButtonColor.PRIMARY_CONTRAST]: segmentColors.primaryContrastDarken,
            [ButtonColor.LIGHT]: COMMON_THEME_COLORS.light,
            [ButtonColor.MONO_900]: segmentColors.mono900
          }
        },
        focus: {
          backgroundColor: 'transparent',
          BUTTON_COLOR_TO_COLOR: {
            [ButtonColor.DARK]: COMMON_THEME_COLORS.dark,
            [ButtonColor.SECONDARY_CONTRAST]: segmentColors.secondaryContrastDarken,
            [ButtonColor.PRIMARY_CONTRAST]: segmentColors.primaryContrastDarken,
            [ButtonColor.LIGHT]: COMMON_THEME_COLORS.light,
            [ButtonColor.MONO_900]: segmentColors.mono900
          }
        },
        disabled: {
          color: segmentColors.mono500,
          background: palette.transparent,
          iconColor: IconColor.MONO_500
        },
        BUTTON_SIZE_TO_LINE_HEIGHT: {
          [ButtonSize.SMALL]: LineHeight.HEIGHT_16,
          [ButtonSize.NORMAL]: LineHeight.HEIGHT_24
        },
        BUTTON_COLOR_TO_COLOR: {
          [ButtonColor.DARK]: COMMON_THEME_COLORS.dark,
          [ButtonColor.SECONDARY_CONTRAST]: segmentColors.secondaryContrast,
          [ButtonColor.PRIMARY_CONTRAST]: segmentColors.primaryContrast,
          [ButtonColor.LIGHT]: COMMON_THEME_COLORS.light,
          [ButtonColor.MONO_900]: segmentColors.mono900
        },
        BUTTON_COLOR_TO_ICON_COLOR: {
          [ButtonColor.DARK]: IconColor.DARK,
          [ButtonColor.MONO_900]: IconColor.MONO_900,
          [ButtonColor.SECONDARY_CONTRAST]: IconColor.SECONDARY_CONTRAST,
          [ButtonColor.PRIMARY_CONTRAST]: IconColor.PRIMARY_CONTRAST,
          [ButtonColor.LIGHT]: IconColor.LIGHT
        }
      },
      BUTTON_SIZE_TO_HEIGHT: {
        [ButtonSize.SMALL]: 32,
        [ButtonSize.NORMAL]: 44
      },
      BUTTON_SIZE_TO_ICON_SIZE: {
        [ButtonSize.SMALL]: IconSize.SMALL,
        [ButtonSize.NORMAL]: IconSize.NORMAL
      },
      isLoadingIndicatorVisible: true
    },
    Table: {
      Head: {
        borderBottom: px(2)
      },
      HeadCell: {
        paddingBottom: {
          mobile: Spacing.SPACE_8,
          desktop: Spacing.SPACE_4
        },
        color: segmentColors.mono600,
        fontSize: FontSize.SIZE_12,
        fontWeight: FontWeight.WEIGHT_600,
        lineHeight: LineHeight.HEIGHT_16,
        textTransform: 'none'
      },
      HeadCellSortable: {
        sortIcon: {
          primaryColor: {
            active: IconColor.SECONDARY_CONTRAST,
            inactive: IconColor.MONO_600
          },
          SORT_DIRECTION_TO_ICON: {
            [TableSortDirection.ASCENDING]: {
              Icon: {
                iconComponent: ChevronUp
              }
            },
            [TableSortDirection.DESCENDING]: {
              Icon: {
                iconComponent: ChevronDown
              }
            }
          }
        }
      },
      Body: {
        scaleXValue: null
      },
      Row: {
        height: 56,
        isBorderAfterLastRow: false,
        isBorderBelowRowAfterExpanding: false,
        shadowHeight: 3,
        BACKGROUND_COLOR_VARIANT_TO_BORDER_COLOR: {
          [TableBackgroundColorVariant.WHITE]: segmentColors.mono500,
          [TableBackgroundColorVariant.GRAY]: segmentColors.mono500,
          [TableBackgroundColorVariant.BLACK]: segmentColors.mono600,
          [TableBackgroundColorVariant.ZEUS_BLACK]: segmentColors.mono600
        },
        hover: {
          TABLE_COLOR_TO_HOVER_BACKGROUND_COLOR: {
            [TableBackgroundColorVariant.GRAY]: segmentColors.mono100,
            [TableBackgroundColorVariant.WHITE]: segmentColors.mono200,
            [TableBackgroundColorVariant.BLACK]: segmentColors.mono900,
            [TableBackgroundColorVariant.ZEUS_BLACK]: palette.solidBlack
          }
        },
        expanded: {
          TABLE_COLOR_TO_EXPANDED_BACKGROUND_COLOR: {
            [TableBackgroundColorVariant.GRAY]: segmentColors.mono100,
            [TableBackgroundColorVariant.WHITE]: segmentColors.mono200,
            [TableBackgroundColorVariant.BLACK]: segmentColors.mono900,
            [TableBackgroundColorVariant.ZEUS_BLACK]: palette.solidBlack
          },
          boxShadow: {
            [TableBackgroundColorVariant.GRAY]: shadows.shadow40,
            [TableBackgroundColorVariant.WHITE]: shadows.shadow40,
            [TableBackgroundColorVariant.BLACK]: `4px 4px 0 0 rgba(${hexToRgb(palette.solidWhite)}, 0.08)`,
            [TableBackgroundColorVariant.ZEUS_BLACK]: `4px 4px 0 0 rgba(${hexToRgb(palette.solidWhite)}, 0.08)`
          }
        }
      },
      Cell: {
        fontSize: 12
      },
      closeButton: {
        transformXValue: null,
        positionRightValue: 0,
        IconButton: {
          iconComponent: Cross,
          iconColor: IconColor.SECONDARY_CONTRAST,
          iconSize: IconSize.NORMAL,
          buttonType: IconButtonType.TRANSPARENT
        }
      },
      mobile: {
        overflowX: 'auto',
        slidingPanel: {
          background: palette.solidWhite
        }
      }
    },
    Typography: {
      DEFAULT_FONT_SIZE: {
        h1_large: [32, 48],
        h1: [32, 40],
        h2: [24, 24],
        h3: [20, 20],
        h4: [16, 16],
        h5: [16, 16],
        body: [16, 16],
        body2: [14, 14],
        small: [12, 12],
        overline: [10, 10],
        span: [16, 16],
        p: [16, 16],
        cta_button: [16, 16],
        a: [16, 16],
        label: [16, 16],
        div: [16, 16]
      },
      AMOUNT_FRACTION_PART_FONT_SIZE: {
        desktop: {
          h1_large: 24,
          h1: 24,
          h2: 16,
          h3: 16,
          h4: 16,
          h5: 16,
          body: 16,
          body2: 14,
          p: 16,
          span: 16,
          small: 12,
          overline: 10,
          a: 16,
          div: 16,
          cta_button: 16,
          label: 16
        },
        mobile: {
          h1_large: 24,
          h1: 24,
          h2: 16,
          h3: 16,
          h4: 16,
          h5: 16,
          body: 16,
          body2: 14,
          p: 16,
          span: 16,
          small: 12,
          overline: 10,
          a: 16,
          div: 16,
          cta_button: 16,
          label: 16
        }
      },
      TEXT_TYPE_TO_FONT_FAMILY_OVERRIDE: {
        h1_large: THEME_FONTS.fontFamilySecondary,
        h1: THEME_FONTS.fontFamilySecondary,
        h2: THEME_FONTS.fontFamilySecondary,
        h3: THEME_FONTS.fontFamilySecondary,
        h4: THEME_FONTS.fontFamilyPrimary,
        h5: THEME_FONTS.fontFamilyPrimary,
        body: THEME_FONTS.fontFamilyPrimary,
        body2: THEME_FONTS.fontFamilyPrimary,
        small: THEME_FONTS.fontFamilyPrimary,
        overline: THEME_FONTS.fontFamilyPrimary,
        cta_button: THEME_FONTS.fontFamilySecondary,
        p: THEME_FONTS.fontFamilyPrimary,
        a: THEME_FONTS.fontFamilyPrimary,
        div: THEME_FONTS.fontFamilyPrimary,
        label: THEME_FONTS.fontFamilyPrimary,
        span: THEME_FONTS.fontFamilyPrimary
      },
      TEXT_TYPE_TO_FONT_WEIGHT: {
        h1_large: FontWeight.WEIGHT_600,
        h1: FontWeight.WEIGHT_600,
        h2: FontWeight.WEIGHT_600,
        h3: FontWeight.WEIGHT_600,
        h4: FontWeight.WEIGHT_600,
        h5: FontWeight.WEIGHT_600,
        body: FontWeight.WEIGHT_500,
        body2: FontWeight.WEIGHT_500,
        small: FontWeight.WEIGHT_600,
        overline: FontWeight.WEIGHT_600,
        cta_button: FontWeight.WEIGHT_600,
        a: FontWeight.WEIGHT_600,
        p: FontWeight.WEIGHT_500,
        div: FontWeight.WEIGHT_500,
        span: FontWeight.WEIGHT_500,
        label: FontWeight.WEIGHT_500
      },
      TEXT_TYPE_TO_LINE_HEIGHT_PX: {
        h1_large: [40, 60],
        h1: [40, 48],
        h2: [28, 28],
        h3: [24, 24],
        h4: [24, 24],
        h5: [24, 24],
        body: [24, 24],
        body2: [20, 20],
        small: [16, 16],
        overline: [16, 16],
        div: [24, 24],
        p: [24, 24],
        span: [24, 24],
        label: [24, 24],
        cta_button: [24, 24],
        a: [24, 24]
      },
      TEXT_TYPE_TO_LETTER_SPACING: {
        h1_large: -1,
        h1: -1,
        h2: -0.5,
        h3: 0,
        h4: 0,
        h5: 0,
        body: 0,
        body2: 0,
        small: 0,
        overline: 0,
        a: 0,
        div: 0,
        p: 0,
        span: 0,
        label: 0,
        cta_button: 0.2
      },
      FONT_COLOR_TO_COLOR: {
        [FontColor.MONO_900]: segmentColors.mono900,
        [FontColor.MONO_600]: segmentColors.mono600,
        [FontColor.MONO_500]: segmentColors.mono500,
        [FontColor.MONO_100]: segmentColors.mono100,
        [FontColor.DARK]: COMMON_THEME_COLORS.dark,
        [FontColor.LIGHT]: COMMON_THEME_COLORS.light,
        [FontColor.POSITIVE_CONTRAST]: segmentColors.positiveContrast,
        [FontColor.NEGATIVE_CONTRAST]: segmentColors.negativeContrast,
        [FontColor.ATTENTION_CONTRAST]: segmentColors.attentionContrast
      }
    },
    UnorderedList: {
      ListItem: {
        character: '■',
        color: segmentColors.secondaryContrast
      },
      LIST_ITEM_SIZE_TO_LINE_HEIGHT: {
        [UnorderedListLineHeight.SMALL]: 12,
        [UnorderedListLineHeight.MEDIUM]: 20
      }
    },
    OrderedList: {
      ListItem: {
        color: segmentColors.secondaryContrast
      }
    },
    ButtonLayout: {
      mobile: {
        verticalSpacing: Spacing.SPACE_24
      },
      desktop: {
        horizontalPadding: Spacing.SPACE_16
      },
      fullWidth: 240
    },
    HorizontalLine: {
      color: segmentColors.mono500
    },
    StatusIndicator: {
      STATUS_INDICATOR_COLOR_TO_COLOR: {
        [StatusIndicatorColor.ATTENTION_CONTRAST]: segmentColors.attentionContrast,
        [StatusIndicatorColor.POSITIVE_CONTRAST]: segmentColors.positiveContrast,
        [StatusIndicatorColor.NEGATIVE_CONTRAST]: segmentColors.negativeContrast,
        [StatusIndicatorColor.MONO_500]: segmentColors.mono500
      }
    },
    Link: {
      fontWeight: FontWeight.WEIGHT_600,
      LINK_COLOR_TO_HOVER_COLOR: {
        [LinkColor.SECONDARY_CONTRAST]: segmentColors.secondaryContrastDarken,
        [LinkColor.DARK]: COMMON_THEME_COLORS.dark,
        [LinkColor.LIGHT]: COMMON_THEME_COLORS.light,
        [LinkColor.MONO_900]: segmentColors.mono900
      },
      LINK_COLOR_TO_COLOR: {
        [LinkColor.SECONDARY_CONTRAST]: segmentColors.secondaryContrast,
        [LinkColor.DARK]: COMMON_THEME_COLORS.dark,
        [LinkColor.LIGHT]: COMMON_THEME_COLORS.light,
        [LinkColor.MONO_900]: segmentColors.mono900
      }
    },
    IconButton: {
      disabledTextColor: segmentColors.mono500,
      disabledTextOpacity: IconButtonBackgroundOpacity.OPACITY_30,
      primary: {
        isPressingEffectEnabled: true,
        isRippleEffectEnabled: true,
        iconColor: IconColor.LIGHT,
        border: 'none',
        disabled: {
          backgroundColor: segmentColors.mono500,
          iconColor: IconColor.LIGHT
        },
        COLOR_TO_BACKGROUND_COLOR: {
          [IconButtonColor.SECONDARY]: segmentColors.secondary,
          [IconButtonColor.PRIMARY]: segmentColors.primary
        },
        COLOR_TO_BACKGROUND_COLOR_FOCUSED: {
          [IconButtonColor.SECONDARY]: segmentColors.secondaryDarken,
          [IconButtonColor.PRIMARY]: segmentColors.primaryDarken
        },
        COLOR_TO_BACKGROUND_COLOR_HOVERED: {
          [IconButtonColor.SECONDARY]: segmentColors.secondaryDarken,
          [IconButtonColor.PRIMARY]: segmentColors.primaryDarken
        },
        ICON_SIZE_TO_SIZE: {
          [IconButtonIconSize.SIZE_16]: 32,
          [IconButtonIconSize.SIZE_24]: 48
        },
        ICON_SIZE_TO_TOUCH_AREA_SIZE: {
          [IconButtonIconSize.SIZE_16]: 44,
          [IconButtonIconSize.SIZE_24]: 48
        }
      },
      secondary: {
        isPressingEffectEnabled: true,
        isRippleEffectEnabled: true,
        backgroundColor: palette.transparent,
        iconColor: IconColor.SECONDARY_CONTRAST,
        border: `solid 1.5px ${segmentColors.mono500}`,
        disabled: {
          backgroundColor: palette.transparent,
          iconColor: IconColor.MONO_500
        },
        focused: {
          backgroundColor: segmentColors.mono500
        },
        hovered: {
          backgroundColor: segmentColors.mono500
        },
        ICON_SIZE_TO_SIZE: {
          [IconButtonIconSize.SIZE_16]: 32,
          [IconButtonIconSize.SIZE_24]: 48
        },
        ICON_SIZE_TO_TOUCH_AREA_SIZE: {
          [IconButtonIconSize.SIZE_16]: 44,
          [IconButtonIconSize.SIZE_24]: 48
        }
      },
      transparent: {
        isPressingEffectEnabled: true,
        isRippleEffectEnabled: true,
        backgroundColor: palette.transparent,
        iconColor: IconColor.SECONDARY_CONTRAST,
        border: 'none',
        disabled: {
          backgroundColor: palette.transparent,
          COLOR_TO_ICON_COLOR: {
            [IconColor.LIGHT]: IconColor.LIGHT,
            [IconColor.DARK]: IconColor.DARK
          },
          opacity: IconButtonBackgroundOpacity.OPACITY_30
        },
        focused: {
          backgroundColor: segmentColors.mono500,
          COLOR_TO_BACKGROUND_COLOR: {
            [IconColor.PRIMARY_CONTRAST]: COMMON_THEME_COLORS.dark,
            [IconColor.LIGHT]: COMMON_THEME_COLORS.dark,
            [IconColor.DARK]: COMMON_THEME_COLORS.light
          },
          COLOR_TO_ICON_COLOR: {
            [IconColor.PRIMARY_CONTRAST]: IconColor.LIGHT,
            [IconColor.LIGHT]: IconColor.LIGHT,
            [IconColor.DARK]: IconColor.DARK
          },
          opacity: IconButtonBackgroundOpacity.OPACITY_30
        },
        hovered: {
          backgroundColor: segmentColors.mono500,
          COLOR_TO_BACKGROUND_COLOR: {
            [IconColor.PRIMARY_CONTRAST]: COMMON_THEME_COLORS.dark,
            [IconColor.LIGHT]: COMMON_THEME_COLORS.dark,
            [IconColor.DARK]: COMMON_THEME_COLORS.light
          },
          COLOR_TO_ICON_COLOR: {
            [IconColor.PRIMARY_CONTRAST]: IconColor.LIGHT,
            [IconColor.LIGHT]: IconColor.LIGHT,
            [IconColor.DARK]: IconColor.DARK
          },
          opacity: IconButtonBackgroundOpacity.OPACITY_30
        },
        ICON_SIZE_TO_SIZE: {
          [IconButtonIconSize.SIZE_16]: 32,
          [IconButtonIconSize.SIZE_24]: 44
        },
        ICON_SIZE_TO_TOUCH_AREA_SIZE: {
          [IconButtonIconSize.SIZE_16]: 44,
          [IconButtonIconSize.SIZE_24]: 44
        }
      }
    },
    Stripe: {
      background: gradients.generateAllBrandColorsLinearToRight(colorMapping)
    },
    Tile: {
      BACKGROUND_COLOR_MAPPING: {
        [TileBackgroundColor.MONO_100]: segmentColors.mono100,
        [TileBackgroundColor.MONO_200]: segmentColors.mono200,
        [TileBackgroundColor.MONO_900]: segmentColors.mono900
      },
      border: 0,
      borderRadius: 0,
      isHoverAnimationEnabled: true,
      isActiveColorEnabled: false,
      normal: {
        boxShadow: `4px 4px 0 0 rgba(${hexToRgb(palette.solidBlack)}, 0.08)`,
        hover: {
          boxShadow: `2px 2px 0 0 rgba(${hexToRgb(palette.solidBlack)}, 0.08)`
        }
      },
      selected: {
        boxShadow: `inset 0 0 0 2px ${segmentColors.secondaryContrast}`,
        hover: {
          boxShadow: `inset 0 0 0 2px ${segmentColors.secondaryContrastDarken}`
        }
      },
      active: {
        backgroundColor: palette.concreteGray30,
        boxShadow: 'none'
      },
      promoted: {
        boxShadow: `4px 4px 0 0 ${palette.confettiYellow}`,
        border: `1px solid ${palette.confettiYellow}`,
        hover: {
          boxShadow: `2px 2px 0 0 ${palette.confettiYellow}`
        }
      },
      iconColor: IconColor.SECONDARY_CONTRAST,
      iconColorHover: reversedPalette[segmentColors.secondaryContrastDarken]
    },
    Radio: {
      RadioGroup: {
        ValidationMessage: {
          color: segmentColors.negativeContrast
        }
      },
      width: 18,
      height: 18,
      checkmarkLeftSpacing: 20,
      borderWidth: '1.5',
      labelSpacing: `${px(8)} ${px(12)}`,
      checkmarkInner: {
        width: `${px(12)}`,
        height: `${px(12)}`
      },
      borderColor: segmentColors.mono600,
      borderColorHover: segmentColors.secondaryContrastDarken,
      borderColorErrorHover: segmentColors.negativeContrastDarken,
      borderColorError: segmentColors.negativeContrast,
      borderColorChecked: segmentColors.secondaryContrast,
      borderColorCheckedHover: segmentColors.secondaryContrastDarken,
      borderColorDisabled: segmentColors.mono500,
      backgroundColorCheckedHover: segmentColors.secondaryContrastDarken,
      backgroundColorChecked: segmentColors.secondaryContrast,
      backgroundColorDisabled: segmentColors.mono500,
      backgroundColor: palette.transparent,
      isShowSplashEffect: false,
      isFullBackground: false,
      disabledTextColor: FontColor.MONO_600,
      RADIO_LABEL_SIZE_TO_TYPOGRAPHY: {
        [RadioLabelSize.NORMAL]: TypographyType.BODY,
        [RadioLabelSize.SMALL]: TypographyType.SMALL
      },
      RADIO_LABEL_SIZE_TO_FONT_WEIGHT: {
        [RadioLabelSize.NORMAL]: FontWeight.WEIGHT_500,
        [RadioLabelSize.SMALL]: FontWeight.WEIGHT_600
      }
    },
    Checkbox: {
      width: 18,
      height: 18,
      LABEL_SIZE_TO_LINE_HEIGHT: {
        [CheckboxLabelSize.SMALL]: rem(1),
        [CheckboxLabelSize.NORMAL]: rem(1.5)
      },
      LABEL_SIZE_TO_FONT_SIZE: {
        [CheckboxLabelSize.SMALL]: FontSize.SIZE_12,
        [CheckboxLabelSize.NORMAL]: FontSize.SIZE_16
      },
      borderWidth: 1.5,
      checkmarkLeftSpacing: 16,
      errorLabelSpacing: `0 0 0 ${px(34)}`,
      labelSpacing: 0,
      borderRadius: 0,
      labelTextColor: {
        default: segmentColors.mono900,
        disabled: segmentColors.mono600
      },
      isShowSplashEffect: false,
      checkMarkIcon: {
        borderWidth: 1.5,
        borderColor: segmentColors.mono000,
        disabledBorderColor: segmentColors.mono100,
        width: 6,
        height: 11,
        top: 0,
        left: 4
      },
      backgroundColor: {
        checked: segmentColors.secondaryContrast,
        checkedHover: segmentColors.secondaryContrastDarken,
        disabled: 'transparent',
        disabledChecked: segmentColors.mono500,
        default: 'transparent'
      },
      borderColor: {
        color: segmentColors.mono600,
        hover: segmentColors.secondaryContrastDarken,
        error: segmentColors.negativeContrast,
        errorHover: segmentColors.negativeContrastDarken,
        checked: segmentColors.secondaryContrast,
        checkedHover: segmentColors.secondaryContrastDarken,
        disabled: segmentColors.mono500
      }
    },
    Switch: {
      isFocusEffectEnabled: false,
      translateXValue: 11.5,
      translateYValue: '50%',
      borderWidth: 1.5,
      lineHeight: 24,
      label: {
        fontSize: 16
      },
      SWITCH_COLOR_TO_COLORS: {
        [SwitchColor.GREEN]: {
          hover: {
            borderColor: palette.funGreenUIHover,
            background: palette.funGreenUIHover,
            checked: {
              background: palette.funGreenUIHover
            }
          },
          checked: {
            switchCircle: {
              background: palette.funGreenUI
            },
            background: `rgba(${hexToRgb(palette.funGreenUI)}, 0.4)`
          },
          checkmarkHover: palette.funGreenUIHover,
          focusEffect: 'transparent',
          disabled: {
            background: `rgba(${hexToRgb(palette.doveGrayUI)}, 0.4)`,
            switchCircle: {
              backgroundColor: palette.doveGrayUI
            },
            borderColor: 'transparent',
            checked: {
              background: `rgba(${hexToRgb(palette.doveGrayUI)}, 0.4)`,
              switchCircle: {
                background: palette.doveGrayUI
              },
              borderColor: 'transparent'
            }
          },
          labelTextColor: palette.zeusBlackUI
        },
        [SwitchColor.BLUE]: {
          hover: {
            borderColor: segmentColors.secondaryContrastDarken,
            background: segmentColors.secondaryContrastDarken,
            checked: {
              background: segmentColors.secondaryContrastDarken
            }
          },
          checked: {
            switchCircle: {
              background: segmentColors.mono000
            },
            background: segmentColors.secondaryContrast
          },
          checkmarkHover: segmentColors.secondaryContrastDarken,
          focusEffect: palette.transparent,
          disabled: {
            background: palette.transparent,
            switchCircle: {
              backgroundColor: segmentColors.mono500
            },
            borderColor: segmentColors.mono500,
            checked: {
              borderColor: palette.transparent,
              background: segmentColors.mono500,
              switchCircle: {
                background: segmentColors.mono100
              }
            }
          },
          labelTextColor: segmentColors.mono900
        }
      },
      checkmark: {
        height: 'inherit',
        borderRadius: 12,
        borderColor: segmentColors.mono600,
        lineColor: palette.transparent,
        position: {
          top: 0
        },
        before: {
          height: 18,
          width: 18,
          borderRadius: 9,
          boxShadow: 'none',
          backgroundColor: segmentColors.mono600,
          position: {
            bottom: '50%',
            left: 1.5
          }
        },
        after: {
          size: 40,
          opacity: 0
        }
      }
    },
    List: {
      paddingVertical: 0,
      backgroundColor: 'inherit',
      item: {
        padding: `${px(Spacing.SPACE_12)} ${px(Spacing.SPACE_16)}`,
        hover: {
          backgroundColor: segmentColors.mono500
        },
        preSelected: {
          backgroundColor: segmentColors.mono500,
          hover: {
            backgroundColor: segmentColors.mono500
          }
        },
        selected: {
          fontWeight: FontWeight.WEIGHT_500
        },
        disabled: {
          color: segmentColors.mono500
        }
      },
      subheader: {
        margin: `0 ${px(Spacing.SPACE_16)}`,
        padding: `${px(Spacing.SPACE_16)} ${Spacing.SPACE_0} ${px(Spacing.SPACE_8)}`
      }
    },
    Menu: {
      isCloseButtonVisibleByDefault: false,
      mobileCloseItemPosition: 'top',
      isMobileStripeVisible: true,
      borderRadius: 0,
      boxShadow: segmentShadows.common,
      paddingY: [Spacing.SPACE_24, Spacing.SPACE_0],
      item: {
        color: segmentColors.mono900,
        disabled: {
          color: segmentColors.mono500,
          iconColor: segmentColors.mono500
        },
        Icon: {
          iconComponent: Cross,
          color: IconColor.SECONDARY_CONTRAST,
          size: IconSize.NORMAL
        },
        hover: {
          backgroundColor: segmentColors.mono500
        },
        preSelected: {
          backgroundColor: segmentColors.mono500,
          hover: {
            backgroundColor: segmentColors.mono500
          }
        },
        mobile: {
          margin: 0
        }
      },
      COLOR_TO_BACKGROUND_COLOR: {
        [MenuColor.MONO_100]: segmentColors.mono100,
        [MenuColor.MONO_200]: segmentColors.mono200
      }
    },
    MegaMenu: {
      backgroundColor: segmentColors.mono100,
      hoverColor: segmentColors.secondaryContrastDarken,
      color: segmentColors.mono900,
      activeColor: segmentColors.secondaryContrast,
      borderColor: segmentColors.mono500,
      shadow: segmentShadows.common
    },
    Select: {
      backgroundColor: segmentColors.mono000,
      isLineRippleEnabled: false,
      height: 62,
      maxHeight: 88,
      borderWidth: 2,
      borderColor: segmentColors.mono500,
      borderRadius: 0,
      disabled: {
        backgroundColor: segmentColors.mono500,
        color: segmentColors.mono600,
        icon: {
          isHidden: false,
          primaryColor: IconColor.MONO_600
        }
      },
      focused: {
        borderColor: segmentColors.secondaryContrast
      },
      label: {
        color: segmentColors.mono600,
        fontWeight: FontWeight.WEIGHT_500,
        lineHeight: LineHeight.HEIGHT_24,
        focus: {
          color: segmentColors.mono900
        },
        raised: {
          fontWeight: FontWeight.WEIGHT_600
        }
      },
      decoration: {
        borderColor: reversedPalette[segmentColors.mono500]
      },
      icon: {
        color: IconColor.SECONDARY_CONTRAST,
        size: IconSize.NORMAL
      },
      value: {
        fontWeight: FontWeight.WEIGHT_500,
        lineHeight: px(LineHeight.HEIGHT_24),
        paddingBottom: 11,
        additionalText: {
          color: FontColor.MONO_600,
          styledAs: TypographyTypeOrTag.SMALL
        }
      },
      item: {
        borderWidthPadding: px(Spacing.SPACE_24),
        VARIANTS: {
          [SelectMenuItemVariant.GROUP]: {
            minHeight: 48,
            padding: [`${px(Spacing.SPACE_24)} ${px(Spacing.SPACE_16)} ${px(Spacing.SPACE_8)} ${px(Spacing.SPACE_16)}`, `${px(Spacing.SPACE_24)} ${px(Spacing.SPACE_16)} ${px(Spacing.SPACE_8)} ${px(Spacing.SPACE_16)}`],
            margin: Spacing.SPACE_0,
            multiple: {
              maxHeight: 56,
              padding: [`${px(Spacing.SPACE_8)} ${px(Spacing.SPACE_16)}`, `${px(Spacing.SPACE_8)} ${px(Spacing.SPACE_16)}`]
            }
          },
          [SelectMenuItemVariant.ITEM]: {
            minHeight: 56,
            padding: [`${px(Spacing.SPACE_16)}`, `${px(Spacing.SPACE_16)}`],
            margin: Spacing.SPACE_0,
            multiple: {
              indentationSize: [Spacing.SPACE_48, Spacing.SPACE_48]
            }
          }
        },
        variants: {
          group: {
            color: segmentColors.mono600,
            fontSize: FontSize.SIZE_12,
            fontWeight: FontWeight.WEIGHT_600,
            textTransform: 'none',
            multiple: {
              fontSize: FontSize.SIZE_16,
              fontWeight: FontWeight.WEIGHT_500,
              lineHeight: LineHeight.HEIGHT_24,
              color: segmentColors.mono900
            }
          }
        },
        additionalText: {
          color: 'mono_600',
          fontWeight: FontWeight.WEIGHT_600,
          styledAs: TypographyTypeOrTag.SMALL,
          selected: {
            fontWeight: FontWeight.WEIGHT_600
          }
        }
      },
      menu: {
        paddingY: 0,
        isTopCloseButtonVisible: true
      }
    },
    Amount: {
      colorized: {
        color: segmentColors.positiveContrast
      }
    },
    TextField: {
      isLineRippleEnabled: false,
      isValidationResultIconShown: true,
      VARIANT_TO_HEIGHT: {
        [TextFieldVariant.NORMAL]: 62,
        [TextFieldVariant.DENSE]: 48
      },
      border: `${px(2)} solid ${segmentColors.mono500}`,
      borderBottom: `${px(2)} solid ${segmentColors.mono500}`,
      borderRadius: 0,
      focused: {
        borderColor: segmentColors.secondaryContrast
      },
      autofill: {
        boxShadow: `0 0 0 50px rgba(${hexToRgb(segmentColors.secondary)}, .10) inset`,
        color: segmentColors.mono900,
        label: {
          color: segmentColors.mono600
        }
      },
      hover: {
        borderWidth: px(2),
        BORDER_COLOR_TO_HOVER_BORDER_COLOR: {
          [TextFieldBorderColor.MONO_500]: segmentColors.mono600,
          [TextFieldBorderColor.MONO_600]: segmentColors.mono700,
          [TextFieldBorderColor.NEGATIVE]: segmentColors.negativeDarken,
          [TextFieldBorderColor.NEGATIVE_CONTRAST]: segmentColors.negativeContrastDarken
        }
      },
      error: {
        borderColor: segmentColors.negativeContrast
      },
      filled: {
        borderColor: segmentColors.mono600
      },
      dense: {
        label: {
          color: segmentColors.mono600
        }
      },
      label: {
        isInlineInDenseVariant: false,
        color: segmentColors.mono600,
        VARIANT_TO_FONT_WEIGHT: {
          [TextFieldVariant.NORMAL]: FontWeight.WEIGHT_500,
          [TextFieldVariant.DENSE]: FontWeight.WEIGHT_600
        },
        raised: {
          top: -11,
          fontWeight: FontWeight.WEIGHT_600,
          error: {
            color: segmentColors.negativeContrast
          }
        },
        focused: {
          color: segmentColors.mono600
        },
        error: {
          color: segmentColors.negativeContrast
        }
      },
      input: {
        fontWeight: FontWeight.WEIGHT_500,
        lineHeight: px(LineHeight.HEIGHT_24),
        caretColor: segmentColors.secondaryContrast,
        placeholder: {
          color: segmentColors.mono600
        },
        background: segmentColors.mono000,
        disabled: {
          border: `${px(2)} solid ${segmentColors.mono500}`,
          backgroundColor: segmentColors.mono500,
          color: segmentColors.mono600
        }
      },
      helperText: {
        marginTop: 4,
        padding: 0,
        color: segmentColors.mono600,
        error: {
          fontWeight: FontWeight.WEIGHT_600
        }
      }
    },
    PasswordField: {
      lockIcon: Lock
    },
    ValidationMessage: {
      color: segmentColors.negativeContrast,
      fontWeight: FontWeight.WEIGHT_600,
      padding: 0,
      beforeContent: ''
    },
    Tabs: {
      color: {
        normal: segmentColors.mono900,
        active: segmentColors.secondaryContrast,
        hover: segmentColors.secondaryContrastDarken
      }
    },
    Tooltip: {
      defaultIconColorVariant: IconColor.SECONDARY_CONTRAST,
      TOOLTIP_VARIANT_TO_CSS_PROPERTIES: {
        [TooltipVariant.MONO_100]: {
          backgroundColor: segmentColors.mono100,
          color: segmentColors.mono900,
          textColor: FontColor.MONO_900
        },
        [TooltipVariant.MONO_200]: {
          backgroundColor: segmentColors.mono200,
          color: segmentColors.mono900,
          textColor: FontColor.MONO_900
        },
        [TooltipVariant.MONO_900]: {
          backgroundColor: segmentColors.mono900,
          color: segmentColors.mono100,
          textColor: reversedPalette[segmentColors.mono100]
        },
        [TooltipVariant.SECONDARY]: {
          backgroundColor: segmentColors.secondary,
          color: palette.solidWhite,
          textColor: FontColor.LIGHT
        },
        [TooltipVariant.PRIMARY]: {
          backgroundColor: segmentColors.primary,
          color: palette.solidWhite,
          textColor: FontColor.LIGHT
        },
        [TooltipVariant.NEGATIVE]: {
          backgroundColor: segmentColors.negative,
          color: palette.solidWhite,
          textColor: FontColor.LIGHT
        },
        [TooltipVariant.POSITIVE]: {
          backgroundColor: segmentColors.positive,
          color: palette.solidWhite,
          textColor: FontColor.LIGHT
        },
        [TooltipVariant.ATTENTION]: {
          backgroundColor: segmentColors.attention,
          color: palette.zeusBlackUI,
          textColor: FontColor.DARK
        }
      },
      isShowTooltipArrow: true,
      isCloseButtonVisible: false,
      minWidth: 'initial',
      maxWidth: px(155),
      textAlign: TooltipPlacement.LEFT,
      padding: `${px(Spacing.SPACE_16)}`,
      fontSize: `${rem(0.75)}`,
      fontWeight: 600,
      lineHeight: `${rem(1)}`,
      borderRadius: 0,
      boxShadow: segmentShadows.common,
      trigger: {
        iconComponent: QuestionMarkRound
      },
      spacing: Spacing.SPACE_4
    },
    SearchField: {
      leadingDecoration: {
        Icon: {
          primaryColor: IconColor.SECONDARY_CONTRAST,
          size: {
            [TextFieldVariant.NORMAL]: IconSize.NORMAL,
            [TextFieldVariant.DENSE]: IconSize.SMALL
          }
        }
      },
      trailingDecoration: {
        IconButton: {
          iconColor: IconColor.SECONDARY_CONTRAST,
          iconSize: {
            [TextFieldVariant.NORMAL]: IconSize.NORMAL,
            [TextFieldVariant.DENSE]: IconSize.SMALL
          }
        }
      }
    },
    Autocomplete: {
      leadingDecoration: {
        Icon: {
          iconComponent: MagnifyingGlass,
          primaryColor: IconColor.SECONDARY_CONTRAST,
          size: {
            [TextFieldVariant.NORMAL]: IconSize.NORMAL,
            [TextFieldVariant.DENSE]: IconSize.SMALL
          }
        }
      },
      trailingDecoration: {
        IconButton: {
          iconComponent: Cross,
          iconColor: IconColor.SECONDARY_CONTRAST,
          iconSize: {
            [TextFieldVariant.NORMAL]: IconSize.NORMAL,
            [TextFieldVariant.DENSE]: IconSize.SMALL
          }
        }
      },
      option: {
        matching: {
          background: palette.confettiYellow,
          color: 'inherit',
          fontWeight: 'inherit'
        }
      }
    },
    Snackbar: {
      TYPE_TO_COLOR: {
        success: segmentColors.positive,
        error: segmentColors.negative,
        info: segmentColors.secondary
      },
      TYPE_TO_ICON: {
        [SnackbarType.ERROR]: WarningTriangle,
        [SnackbarType.INFO]: InfoRound,
        [SnackbarType.SUCCESS]: TickRound
      },
      borderRadius: ['0', '0'],
      boxShadow: 'none',
      button: {
        type: ButtonType.TERTIARY,
        color: ButtonColor.LIGHT,
        textColor: 'solidWhite',
        spacing: Spacing.SPACE_12
      },
      isIconShow: false,
      display: 'flex',
      padding: {
        top: px(Spacing.SPACE_16),
        bottom: px(Spacing.SPACE_16),
        left: px(Spacing.SPACE_24),
        right: px(Spacing.SPACE_24)
      },
      contentLabel: {
        padding: {
          left: 0
        }
      },
      NotificationContainer: {
        margin: {
          right: px(Spacing.SPACE_40)
        }
      },
      NotificationContentWrapper: {
        fontWeight: FontWeight.WEIGHT_500
      }
    },
    Progress: {
      background: segmentColors.mono500,
      height: 4,
      minVisibleWidth: 14,
      minVisibleValueWidth: 18,
      PROGRESS_COLOR_TO_COLOR: {
        [ProgressColor.PRIMARY_CONTRAST]: segmentColors.primaryContrast,
        [ProgressColor.SECONDARY_CONTRAST]: segmentColors.secondaryContrast,
        [ProgressColor.POSITIVE_CONTRAST]: segmentColors.positiveContrast,
        [ProgressColor.NEGATIVE_CONTRAST]: segmentColors.negativeContrast,
        [ProgressColor.ATTENTION]: segmentColors.attention
      },
      progressValueColor: FontColor.MONO_600,
      iconColor: IconColor.SECONDARY_CONTRAST
    },
    NumberField: {
      trailingDecoration: {
        minusIcon: {
          iconComponent: MinusRound,
          iconColor: IconColor.SECONDARY_CONTRAST
        },
        plusIcon: {
          iconComponent: PlusRound,
          iconColor: IconColor.SECONDARY_CONTRAST
        }
      }
    },
    Dialog: {
      background: {
        color: segmentColors.mono100
      },
      Header: {
        textAlign: 'center',
        isHeaderSpacingOnMobile: false,
        isHeaderMarginOnMobile: false,
        padding: Spacing.SPACE_0
      },
      ActionButtons: {
        align: ButtonLayoutAlignment.CENTER
      },
      shadow: shadows.shadow40,
      padding: px(Spacing.SPACE_24),
      borderRadius: 0,
      IconClose: {
        color: IconColor.SECONDARY_CONTRAST,
        iconComponent: Cross
      },
      overlay: {
        background: {
          color: `rgba(${hexToRgb(palette.solidBlack)}, 0.3)`
        }
      }
    },
    Popover: {
      position: PopoverPosition.BELOW,
      backgroundColor: segmentColors.mono100,
      COLOR_TO_BACKGROUND_COLOR: {
        [PopoverColor.MONO_900]: segmentColors.mono900,
        [PopoverColor.SECONDARY]: segmentColors.secondary,
        [PopoverColor.MONO_200]: segmentColors.mono200,
        [PopoverColor.MONO_100]: segmentColors.mono100,
        [PopoverColor.PRIMARY]: segmentColors.primary,
        [PopoverColor.NEGATIVE]: segmentColors.negative,
        [PopoverColor.POSITIVE]: segmentColors.positive,
        [PopoverColor.ATTENTION]: segmentColors.attention
      },
      boxShadow: segmentShadows.common
    },
    Loader: {
      fillColor: palette.solidWhite,
      strokeColor: palette.black30,
      animationColors: {
        stepColor1: palette.simpleRed,
        stepColor2: palette.zeusBlackUI,
        stepColor3: palette.flushOrange,
        stepColor4: palette.brightRedUI,
        stepColor5: palette.endeavourBlueUI,
        stepColor6: palette.funGreenUI
      }
    },
    Skeleton: {
      baseColor: segmentColors.mono500,
      highlightColor: segmentColors.mono200
    },
    Collapsible: {
      color: reversedPalette[segmentColors.mono600],
      textTransform: TextTransform.NONE,
      ChevronIcon: {
        iconComponent: {
          expanded: ChevronUp,
          collapsed: ChevronDown
        },
        iconColor: IconColor.SECONDARY_CONTRAST,
        iconSize: IconSize.NORMAL
      }
    },
    Footer: {
      linksBackgroundColor: segmentColors.mono100,
      informationBackgroundColor: segmentColors.mono200
    },
    GlobalNotification: {
      COLOR_TO_BACKGROUND_COLOR: {
        [GlobalNotificationType.INFO]: segmentColors.secondary,
        [GlobalNotificationType.SUCCESS]: segmentColors.positive,
        [GlobalNotificationType.ERROR]: segmentColors.negative,
        [GlobalNotificationType.ATTENTION]: segmentColors.attention
      }
    },
    CurrencyField: {
      isDivided: false,
      VARIANT_TO_HEIGHT: {
        [CurrencyFieldVariant.NORMAL]: 82,
        [CurrencyFieldVariant.DENSE]: 62
      },
      input: {
        fontWeight: {
          VARIANT_TO_FONT_WEIGHT: {
            [CurrencyFieldVariant.NORMAL]: FontWeight.WEIGHT_600,
            [CurrencyFieldVariant.DENSE]: FontWeight.WEIGHT_500
          }
        }
      }
    },
    Grow: {
      transitionDuration: 150
    },
    Slider: {
      track: {
        height: 5,
        borderRadius: 0
      },
      trackBefore: {
        backgroundColor: segmentColors.secondaryContrast
      },
      trackAfter: {
        backgroundColor: segmentColors.mono500
      },
      thumb: {
        size: 30,
        borderWidth: 5
      }
    },
    DefinitionList: {
      header: {
        color: reversedPalette[segmentColors.mono600]
      },
      DefinitionListTerm: {
        color: [reversedPalette[segmentColors.mono600], reversedPalette[segmentColors.mono900]]
      }
    },
    BarChart: {
      bar: {
        color: {
          normal: segmentColors.mono500,
          active: segmentColors.secondary
        }
      },
      grid: {
        color: segmentColors.mono500,
        zeroColor: segmentColors.mono600
      },
      tick: {
        color: segmentColors.mono600,
        fontFamily: THEME_FONTS.fontFamilyPrimary
      },
      tooltip: {
        TOOLTIP_VARIANT_TO_CSS_PROPERTIES: {
          [TooltipVariant.MONO_100]: {
            backgroundColor: segmentColors.mono100,
            color: segmentColors.mono900
          },
          [TooltipVariant.MONO_200]: {
            backgroundColor: segmentColors.mono200,
            color: segmentColors.mono900
          },
          [TooltipVariant.MONO_900]: {
            backgroundColor: segmentColors.mono900,
            color: segmentColors.mono100
          },
          [TooltipVariant.SECONDARY]: {
            backgroundColor: segmentColors.secondary,
            color: palette.solidWhite
          }
        },
        minWidth: 'initial',
        maxWidth: `${px(155)}`,
        padding: `${px(Spacing.SPACE_8)} ${px(Spacing.SPACE_12)}`,
        borderRadius: 0,
        boxShadow: segmentShadows.common,
        pointerBoxShadow: segmentShadows.common,
        fontFamily: THEME_FONTS.fontFamilyPrimary
      },
      referenceLine: {
        color: palette.flushOrange,
        label: {
          backgroundColor: palette.flushOrange,
          color: palette.solidWhite
        }
      },
      legend: {
        color: palette.flushOrange
      }
    },
    LineChart: {
      line: {
        color: segmentColors.mono500
      },
      grid: {
        color: segmentColors.mono500,
        zeroColor: segmentColors.mono600
      },
      tick: {
        color: segmentColors.mono600,
        fontFamily: THEME_FONTS.fontFamilyPrimary
      },
      tooltip: {
        TOOLTIP_VARIANT_TO_CSS_PROPERTIES: {
          [TooltipVariant.MONO_100]: {
            backgroundColor: segmentColors.mono100,
            color: segmentColors.mono900
          },
          [TooltipVariant.MONO_200]: {
            backgroundColor: segmentColors.mono200,
            color: segmentColors.mono900
          },
          [TooltipVariant.MONO_900]: {
            backgroundColor: segmentColors.mono900,
            color: segmentColors.mono100
          },
          [TooltipVariant.SECONDARY]: {
            backgroundColor: segmentColors.secondary,
            color: palette.solidWhite
          }
        },
        minWidth: 'initial',
        maxWidth: `${px(155)}`,
        padding: `${px(Spacing.SPACE_8)} ${px(Spacing.SPACE_12)}`,
        borderRadius: 0,
        boxShadow: segmentShadows.common,
        pointerBoxShadow: segmentShadows.common,
        fontFamily: THEME_FONTS.fontFamilyPrimary
      },
      point: {
        color: segmentColors.mono100,
        border: segmentColors.mono900
      },
      referenceLine: {
        color: palette.flushOrange,
        label: {
          backgroundColor: palette.flushOrange,
          color: palette.solidWhite
        }
      },
      legend: {
        color: palette.flushOrange
      }
    },
    PieChart: {
      emptyState: {
        color: segmentColors.mono500
      }
    },
    ValueChangeIndicator: {
      fontWeight: FontWeight.WEIGHT_600,
      color: segmentColors.mono900,
      dotColor: segmentColors.mono500,
      status: {
        positive: segmentColors.positiveContrast,
        negative: segmentColors.negativeContrast,
        stable: segmentColors.mono600
      },
      layout: {
        extra_small: {
          fontSize: FontSize.SIZE_10,
          caret: {
            width: px(3.5),
            height: px(6.5),
            marginLeft: px(4),
            marginRight: px(4)
          },
          dot: {
            width: px(4),
            marginLeft: px(6),
            marginRight: px(6)
          }
        },
        small: {
          fontSize: FontSize.SIZE_12,
          caret: {
            width: px(3.5),
            height: px(6.5),
            marginLeft: px(4),
            marginRight: px(4)
          },
          dot: {
            width: px(4),
            marginLeft: px(6),
            marginRight: px(6)
          }
        },
        regular: {
          fontSize: FontSize.SIZE_16,
          caret: {
            width: px(5.25),
            height: px(9),
            marginLeft: px(5),
            marginRight: px(5)
          },
          dot: {
            width: px(6),
            marginLeft: px(7),
            marginRight: px(7)
          }
        },
        large: {
          fontSize: FontSize.SIZE_24,
          caret: {
            width: px(7),
            height: px(12),
            marginLeft: px(7),
            marginRight: px(7)
          },
          dot: {
            width: px(8),
            marginLeft: px(8),
            marginRight: px(8)
          }
        }
      }
    },
    MapView: {
      mapColor: segmentColors.mono500
    },
    Symbol: {
      symbolColorToPaletteColor: {
        [SymbolColor.PRIMARY_CONTRAST]: segmentColors.primaryContrast,
        [SymbolColor.SECONDARY_CONTRAST]: segmentColors.secondaryContrast,
        [SymbolColor.POSITIVE_CONTRAST]: segmentColors.positiveContrast,
        [SymbolColor.NEGATIVE_CONTRAST]: segmentColors.negativeContrast,
        [SymbolColor.ATTENTION_CONTRAST]: segmentColors.attentionContrast,
        [SymbolColor.MONO_500]: segmentColors.mono500,
        [SymbolColor.MONO_600]: segmentColors.mono600,
        [SymbolColor.MONO_900]: segmentColors.mono900
      },
      defaultVariant: segmentColors.mono600
    }
  };
}